import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useBuyerAuth } from '../contexts/BuyerAuthContext';
import { User, LogOut, Menu, X } from 'lucide-react';
import WhatsAppFloat from './WhatsAppFloat';
import './BuyerLayout.css';
import { CITY_SEO } from '../pages/buyer/CityLanding';

const FOOTER_CITIES = Object.keys(CITY_SEO).filter(slug => !CITY_SEO[slug].noindex);

const BuyerLayout = ({ children }) => {
  const { buyer, logout } = useBuyerAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/vehicles', label: 'Browse Vehicles' },
    // '/new-taxi' returns here when the section's rework is done
    { path: '/taxi-valuation', label: 'Taxi Valuation' },
    { path: '/blog', label: 'Blog' },
    { path: '/about', label: 'About Us' },
  ];

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="buyer-layout" data-testid="buyer-layout">
      <header className="buyer-topnav">
        <Link to="/" className="buyer-logo" data-testid="buyer-logo">
          <span className="buyer-logo-text">Buy Sell <span className="logo-accent">Taxi</span></span>
        </Link>
        
        {/* Desktop Nav */}
        <nav className="buyer-nav-links desktop-only">
          {navLinks.map(l => (
            <Link
              key={l.path}
              to={l.path}
              className={`buyer-nav-link ${location.pathname === l.path ? 'active' : ''}`}
              data-testid={`nav-${l.path === '/' ? 'home' : l.path.slice(1)}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        
        {/* Desktop Right */}
        <div className="buyer-nav-right desktop-only">
          {buyer ? (
            <>
              <span className="buyer-name">{buyer.name || 'My Account'}</span>
              <button className="buyer-auth-btn" onClick={logout} data-testid="buyer-logout">
                <LogOut size={16} /> Logout
              </button>
            </>
          ) : (
            <Link to="/buyer/login" className="buyer-auth-btn" data-testid="buyer-login-btn">
              <User size={16} /> Login
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button className="mobile-menu-btn mobile-only" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen}>
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="buyer-mobile-menu" data-testid="buyer-mobile-menu">
          <nav className="buyer-mobile-nav">
            {navLinks.map(l => (
              <Link
                key={l.path}
                to={l.path}
                className={`buyer-mobile-link ${location.pathname === l.path ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="buyer-mobile-auth">
            {buyer ? (
              <>
                <span className="buyer-mobile-name">{buyer.name || 'My Account'}</span>
                <button className="buyer-auth-btn" onClick={() => { logout(); handleNavClick(); }}>
                  <LogOut size={16} /> Logout
                </button>
              </>
            ) : (
              <Link to="/buyer/login" className="buyer-auth-btn" onClick={handleNavClick}>
                <User size={16} /> Login / Register
              </Link>
            )}
          </div>
        </div>
      )}

      <main>{children}</main>
      <WhatsAppFloat />

      <footer className="buyer-footer">
        <div className="buyer-footer-inner">
          <div className="footer-sell-cta">
            <div>
              {/* A styled <p>, not an <h3>: this footer is on every page, so as a
                  heading it put a sales prompt into the outline of all 133 posts. */}
              <p className="footer-sell-title">Want to sell your vehicle?</p>
              <p>List your taxi, van, or bus on Buy Sell Taxi and reach buyers looking for commercial vehicles.</p>
            </div>
            <Link to="/seller/register" className="footer-sell-btn">Register as Seller</Link>
          </div>
          <div className="footer-columns">
            <div className="footer-col">
              <h4>Quick Links</h4>
              <Link to="/">Home</Link>
              <Link to="/vehicles">Browse Vehicles</Link>
              <Link to="/blog">Blog</Link>
              <Link to="/about">About Us</Link>
              <Link to="/sell-taxi">Sell Your Taxi</Link>
              <Link to="/taxi-valuation">Taxi Valuation Calculator</Link>
              <Link to="/t-permit">T-Permit Guide</Link>
              <Link to="/faq">FAQ</Link>
              <Link to="/seller/login">Seller Login</Link>
            </div>
            <div className="footer-col">
              <h4>Buy Taxi By City</h4>
              {/* Indexable city pages only, straight from CITY_SEO — this footer
                  is on every page, and it linked all 16 cities including the 8
                  noindexed for having no local stock. Un-noindex a city there
                  and it appears here; nothing else to update. */}
              {FOOTER_CITIES.map(slug => (
                <Link key={slug} to={`/buy-taxi-${slug}`}>
                  Buy Taxi in {slug.charAt(0).toUpperCase() + slug.slice(1)}
                </Link>
              ))}
            </div>
            <div className="footer-col">
              <h4>Popular Guides</h4>
              <Link to="/blog/how-to-buy-used-taxi-india">How to Buy a Used Taxi</Link>
              <Link to="/blog/how-to-sell-taxi-fast-india">How to Sell Your Taxi</Link>
              <Link to="/blog/cng-vs-diesel-taxi-india">CNG vs Diesel Taxi</Link>
              <Link to="/blog/best-cars-ola-uber-india-2026">Best Cars for Ola/Uber</Link>
              <Link to="/blog/commercial-vehicle-loan-guide-india">CV Loan Guide</Link>
            </div>
            <div className="footer-col">
              <h4>Connect</h4>
              <a href="https://www.instagram.com/buyselltaxi/" target="_blank" rel="noopener noreferrer">Instagram</a>
              <a href="https://www.youtube.com/@buyselltaxi" target="_blank" rel="noopener noreferrer">YouTube</a>
              <a href="https://www.linkedin.com/company/buyselltaxi" target="_blank" rel="noopener noreferrer">LinkedIn</a>
              <a href="https://maps.google.com/?cid=15507023363500128633" target="_blank" rel="noopener noreferrer">Google Business</a>
            </div>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} Buy Sell Taxi. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BuyerLayout;
